import { useState, useEffect } from 'react';
import {
  Syringe, ShieldCheck, Share2, Users, Calendar, QrCode,
  AlertTriangle, Download, Upload, Plus, ChevronDown, ChevronUp,
  Building2, User, Globe, FileText, Shield, X,
} from 'lucide-react';
import { T } from '@design/index';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi, type VaccinationRecord } from '../api';
import type { PatientCertificateDoc, FamilyMember } from '../types';
import NHCIdBadge from '../components/NHCIdBadge';
import NeoVerifyBadge from '../components/NeoVerifyBadge';
import InstitutionShareModal from '../components/InstitutionShareModal';
import Spinner from '../../../components/ui/Spinner';

function fmtDate(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

type SourceFilter = 'all' | 'doctor' | 'government' | 'manual';

const SOURCE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  DOCTOR: { bg: 'bg-teal-50', text: 'text-teal-700', label: 'Doctor/Hospital' },
  GOVERNMENT: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Govt Import' },
  MANUAL: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Manual' },
};

function getSource(c: PatientCertificateDoc): string {
  // `linkedModule === 'COWIN'` was checked here and `linkedModule` is not a
  // field this API sends, so that half of the condition was always false.
  if (c.issuerType === 'GOVERNMENT') return 'GOVERNMENT';
  if (c.issuerType === 'HOSPITAL' || c.issuerType === 'DOCTOR' || c.linkedProviderId) return 'DOCTOR';
  return 'MANUAL';
}

export default function Vaccinations() {
  const [certs, setCerts] = useState<PatientCertificateDoc[]>([]);
  const [family, setFamily] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [nhcId, setNhcId] = useState('');
  const [selectedMember, setSelectedMember] = useState('self');
  const [shareTarget, setShareTarget] = useState<{ id: string; title: string } | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [importExpanded, setImportExpanded] = useState(false);
  const [importNotice, setImportNotice] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [manualProvider, setManualProvider] = useState('');
  const [addedNotice, setAddedNotice] = useState('');
  const [addError, setAddError] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [selfVax, setSelfVax] = useState<VaccinationRecord[]>([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      patientApi.getCertificates({ type: 'VACCINATION_CERTIFICATE' }),
      patientApi.getCertificates({ type: 'VACCINATION_COMPLETION_CERTIFICATE' }),
      patientApi.getFamily(),
      patientApi.getProfile(),
      patientApi.getVaccinations().catch(() => [] as VaccinationRecord[]),
    ]).then(([vax, comp, fam, prof, own]) => {
      if (alive) { setCerts([...vax, ...comp]); setFamily(fam); setNhcId(prof.nhcId); setSelfVax(own); }
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Real save — the record lands on the backend (labelled SELF_REPORTED);
  // a failed save is an error, never a green tick.
  const handleAddVaccine = async () => {
    if (!manualName.trim()) return;
    setAddBusy(true);
    setAddError('');
    try {
      const created = await patientApi.addVaccination({
        vaccineName: manualName.trim(),
        administeredOn: manualDate || undefined,
        administeredAt: manualProvider.trim() || undefined,
      });
      setSelfVax(prev => [created, ...prev]);
      setAddedNotice(`"${created.vaccineName}" recorded on your NeoHealthCard account (self-reported).`);
      setShowAddModal(false);
      setManualName(''); setManualDate(''); setManualProvider('');
      setTimeout(() => setAddedNotice(''), 5000);
    } catch {
      setAddError('Could not save — the server is unreachable. The record was NOT added; please try again.');
    } finally {
      setAddBusy(false);
    }
  };

  const filtered = certs.filter(c => {
    if (sourceFilter === 'all') return true;
    const src = getSource(c);
    if (sourceFilter === 'doctor') return src === 'DOCTOR';
    if (sourceFilter === 'government') return src === 'GOVERNMENT';
    return src === 'MANUAL';
  });

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6" style={govPageContainer()}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="flex items-center gap-2" style={GOV_TYPE.pageTitle}>
            <Syringe className="w-6 h-6 text-teal-600" /> Vaccination Records
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Vaccination certificates & schedule linked to your NHC ID</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAddModal(true)} className="px-3 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Add Vaccine
          </button>
          {nhcId && <NHCIdBadge nhcId={nhcId} />}
        </div>
      </div>

      {/* Family switcher */}
      {family.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <Users className="w-4 h-4 text-gray-400 shrink-0" />
          <button onClick={() => setSelectedMember('self')}
            className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap ${selectedMember === 'self' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Self
          </button>
          {family.map(f => (
            <button key={f.id} onClick={() => setSelectedMember(f.id)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap ${selectedMember === f.id ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {f.name}
            </button>
          ))}
        </div>
      )}

      {addedNotice && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 flex items-center gap-2 text-xs text-teal-800">
          <ShieldCheck className="w-4 h-4 text-teal-600 shrink-0" />
          <span>{addedNotice}</span>
        </div>
      )}

      {/* Import Actions */}
      <div className="card overflow-hidden">
        <button onClick={() => setImportExpanded(!importExpanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4 text-teal-600" />
            <span className="text-xs font-semibold text-gray-900">Import Vaccination Records</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Demo</span>
          </div>
          {importExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {importExpanded && (
          <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-2 flex gap-2 text-[10px] text-teal-800">
              <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <p>Import requires your consent. Records are verified and linked to your NHC ID. Real ABDM/CoWIN integration is pending — these are demo placeholders.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button onClick={() => setImportNotice('ABDM/ABHA import is a demo placeholder. Real integration requires ABDM API credentials.')}
                className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-left">
                <Globe className="w-5 h-5 text-blue-600 shrink-0" />
                <div>
                  <p className="text-[11px] font-semibold text-gray-900">ABDM / ABHA</p>
                  <p className="text-[9px] text-gray-500">Ayushman Bharat Digital Mission</p>
                </div>
              </button>
              <button onClick={() => setImportNotice('CoWIN import is a demo placeholder. Real integration requires CoWIN API access via beneficiary ID.')}
                className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-left">
                <Building2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <div>
                  <p className="text-[11px] font-semibold text-gray-900">CoWIN</p>
                  <p className="text-[9px] text-gray-500">COVID-19 vaccination records</p>
                </div>
              </button>
              <button onClick={() => setImportNotice('Upload your vaccination certificate (PDF/image). It will be verified and linked to your NHC ID.')}
                className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-left">
                <Upload className="w-5 h-5 text-gray-600 shrink-0" />
                <div>
                  <p className="text-[11px] font-semibold text-gray-900">Upload</p>
                  <p className="text-[9px] text-gray-500">Manual document upload</p>
                </div>
              </button>
            </div>
            {importNotice && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 flex gap-2 text-[10px] text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <p>{importNotice}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Source filter pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <span className="text-[10px] text-gray-500 shrink-0">Source:</span>
        {([
          { key: 'all' as const, label: `All (${certs.length})`, icon: Syringe },
          { key: 'doctor' as const, label: `Doctor/Hospital (${certs.filter(c => getSource(c) === 'DOCTOR').length})`, icon: User },
          { key: 'government' as const, label: `Govt Import (${certs.filter(c => getSource(c) === 'GOVERNMENT').length})`, icon: Globe },
          { key: 'manual' as const, label: `Manual (${certs.filter(c => getSource(c) === 'MANUAL').length})`, icon: Upload },
        ]).map(f => (
          <button key={f.key} onClick={() => setSourceFilter(f.key)}
            className={`text-[10px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap flex items-center gap-1 transition-colors ${
              sourceFilter === f.key ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}>
            <f.icon className="w-3 h-3" /> {f.label}
          </button>
        ))}
      </div>

      {/* Stats — only the real certificate count; there is no vaccination
          schedule feature yet, so no invented "upcoming/overdue" numbers */}
      <div className="grid grid-cols-1 gap-3">
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{certs.length + selfVax.length}</p>
          <p className="text-xs text-gray-500 mt-0.5">Recorded Vaccinations</p>
        </div>
      </div>

      {/* Due vaccines — honestly not built yet */}
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-amber-500" /> Vaccine Schedule
        </h2>
        <div className="card p-4 text-center">
          <p className="text-sm text-gray-500">Personalised vaccine schedule and due-date reminders are coming soon.</p>
          <p className="text-[10px] text-gray-400 mt-1">Consult your doctor for your current vaccination needs.</p>
        </div>
      </div>

      {/* Self-reported vaccination records (real backend rows, honest label) */}
      {selfVax.length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Syringe className="w-4 h-4 text-teal-500" /> My Recorded Vaccinations
          </h2>
          <div className="space-y-3">
            {selfVax.map(v => (
              <div key={v.id} className="card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-900">{v.vaccineName}</h3>
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Self-reported</span>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-500">
                      {v.administeredOn && <span>Date: {fmtDate(v.administeredOn)}</span>}
                      {v.administeredAt && <span>At: {v.administeredAt}</span>}
                      {v.doseLabel && <span>{v.doseLabel}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Vaccination Certificates */}
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-500" /> Vaccination Certificates
        </h2>
        {filtered.length === 0 ? (
          <div className="text-center py-8">
            <Syringe className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No vaccination certificates {sourceFilter !== 'all' ? 'for this source' : 'yet'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(c => {
              const src = getSource(c);
              const srcMeta = SOURCE_COLORS[src] || SOURCE_COLORS.MANUAL;
              return (
                <div key={c.id} className="card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-gray-900">{c.title}</h3>
                        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${srcMeta.bg} ${srcMeta.text}`}>{srcMeta.label}</span>
                      </div>
                      {c.description && <p className="text-xs text-gray-500 mt-1">{c.description}</p>}
                      <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-500">
                        <span>Issued: {fmtDate(c.issueDate)}</span>
                        {c.issuerName && <span>By: {c.issuerName}</span>}
                      </div>
                    </div>
                    <NeoVerifyBadge status={c.verificationStatus} neoVerifyId={c.neoVerifyId} />
                  </div>
                  <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-100">
                    <button onClick={() => setShareTarget({ id: c.id, title: c.title })}
                      className="text-[11px] text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                      <Share2 className="w-3 h-3" /> Share for School/Travel/Work
                    </button>
                    <button onClick={() => window.print()}
                      className="text-[11px] text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1">
                      <Download className="w-3 h-3" /> Print / PDF
                    </button>
                    {c.neoVerifyId && (
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5 ml-auto">
                        <QrCode className="w-3 h-3" /> QR Available
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p style={GOV_COMPLIANCE.text}>Verified through NeoHealthCard™ · Vaccination records are consent-controlled</p>

      {shareTarget && (
        <InstitutionShareModal
          open={!!shareTarget}
          onClose={() => setShareTarget(null)}
          certificateId={shareTarget.id}
          certificateTitle={shareTarget.title}
        />
      )}

      {/* Add Vaccine Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Plus className="w-4 h-4 text-teal-600" /> Add Vaccine Record</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Vaccine Name</label>
              <input type="text" value={manualName} onChange={e => setManualName(e.target.value)}
                placeholder="e.g. Covishield Dose 2"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Date Administered</label>
              <input type="date" value={manualDate} onChange={e => setManualDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Provider / Hospital</label>
              <input type="text" value={manualProvider} onChange={e => setManualProvider(e.target.value)}
                placeholder="e.g. City Hospital, Mumbai"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30" />
            </div>
            {addError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-[10px] text-red-700 font-medium">{addError}</div>
            )}
            <p className="text-[9px] text-gray-400 leading-relaxed">
              Saved as a <strong>self-reported</strong> record on your NeoHealthCard account — it is not a provider-verified certificate.
            </p>
            <div className="flex gap-2">
              <button onClick={handleAddVaccine} disabled={!manualName.trim() || addBusy}
                className="flex-1 px-4 py-2 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 disabled:opacity-40">
                {addBusy ? 'Saving…' : 'Add Record'}
              </button>
              <button onClick={() => { setShowAddModal(false); setAddError(''); }} className="px-4 py-2 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
